import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { GalleryHorizontal, UserCircle, CreditCard, Car, Plus, X, CheckCircle, MessageSquare } from 'lucide-react';
import {
  RegistrationModal,
  PersonalInfoSection,
  ProfessionalInfoSection,
} from './registration';
import { PhotoCaptureResult } from './PhotoCaptureModal';
import PhotoCaptureModalEnhanced from './PhotoCaptureModalEnhanced';
import PhotoIPCameraModal, { PhotoResult as IPCameraPhotoResult } from './PhotoIPCameraModal';
import PhotoUploadArea from './PhotoUploadArea';
import { AllPhotosEditModal } from './AllPhotosEditModal';
import { LicensePlate } from './ui/LicensePlate';
import type { PhotoResult } from './PhotoUploadModal';
import toast from 'react-hot-toast';
import { usePersonFieldConfig } from '../hooks/usePersonFieldConfig';

interface Department {
  id: string;
  name: string;
}

interface EmployeeFormData {
  first_name: string;
  last_name: string;
  cpf: string;
  rg?: string;
  /** Foreigner identity — see utils/foreigner.ts. */
  is_foreigner?: boolean;
  passport_number?: string;
  /** ISO 3166-1 alpha-2. */
  country?: string;
  email?: string;
  phone?: string;
  birth_date?: string;
  position?: string;
  department_ids?: string[];
  hire_date?: string;
}

interface EmployeeRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    formData: EmployeeFormData;
    photo: PhotoCaptureResult | null;
    documentFrontPhoto?: PhotoCaptureResult | null;
    documentBackPhoto?: PhotoCaptureResult | null;
    vehicleList?: Array<{ plate: string; model: string; color: string }>;
    internalNote?: string;
  }) => Promise<void>;
  departments: Department[];
  editingEmployee?: any;
  isLoading?: boolean;
  customerId?: string | null;
  darkMode?: boolean;
  defaultDepartmentId?: string;
}

export const EmployeeRegistrationModal: React.FC<EmployeeRegistrationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  departments,
  editingEmployee,
  isLoading = false,
  customerId,
  darkMode = false,
  defaultDepartmentId,
}) => {
  const fieldConfig = usePersonFieldConfig(customerId, 'staff');
  const [photo, setPhoto] = useState<PhotoCaptureResult | null>(null);
  const [documentFrontPhoto, setDocumentFrontPhoto] = useState<PhotoCaptureResult | null>(null);
  const [documentBackPhoto, setDocumentBackPhoto] = useState<PhotoCaptureResult | null>(null);
  const [showAllPhotosModal, setShowAllPhotosModal] = useState(false);

  // Identity photo: capture/webcam/ip — PhotoUploadArea owns the method-picker itself
  const [showIdPhotoModal, setShowIdPhotoModal] = useState(false);
  const [idPhotoModalTab, setIdPhotoModalTab] = useState<0 | 1>(0);
  const [showIdPhotoIPModal, setShowIdPhotoIPModal] = useState(false);

  // Doc front: capture/webcam/ip — PhotoUploadArea owns the method-picker itself
  const [showDocFrontModal, setShowDocFrontModal] = useState(false);
  const [docFrontModalTab, setDocFrontModalTab] = useState<0 | 1>(0);
  const [showDocFrontIPModal, setShowDocFrontIPModal] = useState(false);

  // Doc back: capture/webcam/ip — PhotoUploadArea owns the method-picker itself
  const [showDocBackModal, setShowDocBackModal] = useState(false);
  const [docBackModalTab, setDocBackModalTab] = useState<0 | 1>(0);
  const [showDocBackIPModal, setShowDocBackIPModal] = useState(false);

  // Photo zoom lightbox (filled-state click) — shared across identity/doc front/doc back
  const [zoomPhotoUrl, setZoomPhotoUrl] = useState<string | null>(null);
  const [zoomPhotoTitle, setZoomPhotoTitle] = useState<string>('');
  const [zoomPhotoActions, setZoomPhotoActions] = useState<{ onReplace: () => void; onRemove: () => void } | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const departmentIdsRef = useRef<string[]>([]);

  // Vehicle — same "add one vehicle inline" pattern as VisitorRegistrationModal
  const [vehicleList, setVehicleList] = useState<Array<{ plate: string; model: string; color: string }>>([]);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [newVehiclePlate, setNewVehiclePlate] = useState('');
  const [newVehicleModel, setNewVehicleModel] = useState('');
  const [newVehicleColor, setNewVehicleColor] = useState('');

  // Internal note — same "operator-only note" pattern as VisitorRegistrationModal
  const [newEmployeeNote, setNewEmployeeNote] = useState('');

  const {
    register,
    control,
    handleSubmit,
    reset,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<EmployeeFormData>();

  useEffect(() => {
    if (isOpen) {
      if (editingEmployee) {
        const activeRole = editingEmployee.roles?.find((r: any) => r.is_active && r.role_type === 'staff');
        const meta = activeRole?.role_metadata;
        const deptIds: string[] = meta?.department_ids || (meta?.department_id ? [meta.department_id] : editingEmployee.department_id ? [editingEmployee.department_id] : []);
        reset({
          first_name: editingEmployee.first_name || '',
          last_name: editingEmployee.last_name || '',
          cpf: editingEmployee.cpf || '',
          rg: editingEmployee.rg || '',
          email: editingEmployee.email || '',
          phone: editingEmployee.phone || '',
          birth_date: editingEmployee.date_of_birth || '',
          is_foreigner: !!editingEmployee.is_foreigner,
          passport_number: editingEmployee.passport_number || '',
          country: editingEmployee.country || '',
          position: editingEmployee.position || '',
          hire_date: editingEmployee.hire_date || '',
          department_ids: deptIds,
        });
        setSelectedDepartmentIds(deptIds);
        departmentIdsRef.current = deptIds;
      } else {
        const initDeptIds = defaultDepartmentId ? [defaultDepartmentId] : [];
        reset({ first_name: '', last_name: '', cpf: '', rg: '', email: '', phone: '', birth_date: '', is_foreigner: false, passport_number: '', country: '', position: '', hire_date: '', department_ids: initDeptIds });
        setSelectedDepartmentIds(initDeptIds);
        departmentIdsRef.current = initDeptIds;
        setPhoto(null);
        setDocumentFrontPhoto(null);
        setDocumentBackPhoto(null);
        setVehicleList([]);
        setShowVehicleForm(false);
        setNewVehiclePlate('');
        setNewVehicleModel('');
        setNewVehicleColor('');
        setNewEmployeeNote('');
      }
    }
  }, [isOpen, editingEmployee, reset]);

  const handleDepartmentChange = (ids: string[]) => {
    departmentIdsRef.current = ids;
    setSelectedDepartmentIds(ids);
    setValue('department_ids', ids);
  };

  const handleFormSubmit = async (data: EmployeeFormData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const departmentIds = departmentIdsRef.current;
    try {
      await onSubmit({
        formData: { ...data, department_ids: departmentIds },
        photo,
        documentFrontPhoto,
        documentBackPhoto,
        vehicleList,
        internalNote: newEmployeeNote.trim() || undefined,
      });
    } catch (error) {
      console.error('Employee registration failed:', error);
      toast.error('Erro ao salvar funcionário. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setPhoto(null);
    setDocumentFrontPhoto(null);
    setDocumentBackPhoto(null);
    setVehicleList([]);
    setShowVehicleForm(false);
    setNewVehiclePlate('');
    setNewVehicleModel('');
    setNewVehicleColor('');
    setNewEmployeeNote('');
    reset();
    onClose();
  };

  const photoSection = (
    <div className="flex flex-col items-center gap-3 w-full">
      {/* Edit all photos button */}
      <button
        type="button"
        onClick={() => setShowAllPhotosModal(true)}
        disabled={isLoading || isSubmitting}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors w-full justify-center disabled:opacity-50 ${
          darkMode
            ? 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 hover:bg-gray-800'
            : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
        }`}
      >
        <GalleryHorizontal className="h-3.5 w-3.5" />
        Editar todas as fotos
      </button>

      {/* Identity photo — same PhotoUploadArea/labels/icon as the visitor registration UI */}
      {!editingEmployee ? (
        <PhotoUploadArea
          photo={photo}
          onUpload={() => { setIdPhotoModalTab(0); setShowIdPhotoModal(true); }}
          onWebcam={() => { setIdPhotoModalTab(1); setShowIdPhotoModal(true); }}
          onIPCamera={() => setShowIdPhotoIPModal(true)}
          onRemove={() => setPhoto(null)}
          onZoom={(url, title, onReplace, onRemove) => {
            setZoomPhotoUrl(url);
            setZoomPhotoTitle(title);
            setZoomPhotoActions({ onReplace, onRemove });
          }}
          shape="circle"
          aspectRatio="aspect-[4/5]"
          icon={UserCircle}
          label="IDENTIFICAÇÃO"
          title="Foto de Identificação"
          darkMode={darkMode}
        />
      ) : (
        <div className={`w-32 h-40 rounded-xl border-4 flex items-center justify-center ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
          <span className={`text-[10px] text-center px-3 leading-relaxed ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Foto editável na página de detalhes
          </span>
        </div>
      )}

      {/* Document photos — same PhotoUploadArea/labels/icon as the visitor registration UI */}
      <div className="grid grid-cols-2 gap-2 w-full">
        <PhotoUploadArea
          photo={documentFrontPhoto}
          onUpload={() => { setDocFrontModalTab(0); setShowDocFrontModal(true); }}
          onWebcam={() => { setDocFrontModalTab(1); setShowDocFrontModal(true); }}
          onIPCamera={() => setShowDocFrontIPModal(true)}
          onRemove={() => setDocumentFrontPhoto(null)}
          onZoom={(url, title, onReplace, onRemove) => {
            setZoomPhotoUrl(url);
            setZoomPhotoTitle(title);
            setZoomPhotoActions({ onReplace, onRemove });
          }}
          shape="rectangle"
          aspectRatio="aspect-[3/2]"
          icon={CreditCard}
          badge="F"
          label="DOC. FRENTE"
          title="Foto de Documento (Frente)"
          darkMode={darkMode}
        />
        <PhotoUploadArea
          photo={documentBackPhoto}
          onUpload={() => { setDocBackModalTab(0); setShowDocBackModal(true); }}
          onWebcam={() => { setDocBackModalTab(1); setShowDocBackModal(true); }}
          onIPCamera={() => setShowDocBackIPModal(true)}
          onRemove={() => setDocumentBackPhoto(null)}
          onZoom={(url, title, onReplace, onRemove) => {
            setZoomPhotoUrl(url);
            setZoomPhotoTitle(title);
            setZoomPhotoActions({ onReplace, onRemove });
          }}
          shape="rectangle"
          aspectRatio="aspect-[3/2]"
          icon={CreditCard}
          badge="V"
          label="DOC. VERSO"
          title="Foto de Documento (Verso)"
          darkMode={darkMode}
        />
      </div>

      {/* Note section — below photos, same "operator-only note" pattern as VisitorRegistrationModal */}
      <div className={`flex-1 flex flex-col border-t p-3 w-full ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-1.5 mb-2">
          <MessageSquare className={`h-3.5 w-3.5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <span className={`text-[10px] font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Nota Interna
          </span>
          <span className={`text-[10px] ml-auto ${newEmployeeNote.length > 0 ? (darkMode ? 'text-gray-400' : 'text-gray-500') : (darkMode ? 'text-gray-600' : 'text-gray-400')}`}>
            {newEmployeeNote.length}/500
          </span>
        </div>
        <textarea
          value={newEmployeeNote}
          onChange={(e) => setNewEmployeeNote(e.target.value)}
          placeholder="Anotação interna sobre este funcionário..."
          maxLength={500}
          className={`flex-1 w-full rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-blue-500/50 p-2.5 min-h-[80px] ${
            darkMode
              ? 'bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-600 focus:border-blue-500/50'
              : 'bg-white border border-gray-300 text-gray-700 placeholder-gray-400 focus:border-blue-400'
          }`}
        />
        <p className={`text-[10px] mt-1.5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          Visível apenas para operadores
        </p>
      </div>
    </div>
  );

  return (
    <>
      <RegistrationModal
        isOpen={isOpen}
        onClose={handleClose}
        title={editingEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}
        subtitle={editingEmployee ? 'Atualize as informações do funcionário' : 'Preencha os dados do novo funcionário'}
        onSubmit={handleSubmit(handleFormSubmit)}
        isLoading={isLoading || isSubmitting}
        submitLabel={editingEmployee ? 'Atualizar' : 'Criar Funcionário'}
        darkMode={darkMode}
        photoSection={photoSection}
      >
        <PersonalInfoSection
          register={register}
          control={control}
          trigger={trigger}
          setValue={setValue}
          errors={errors}
          disabled={isLoading}
          cpfRequired={false}
          fieldConfig={fieldConfig}
          darkMode={darkMode}
        />
        <ProfessionalInfoSection
          register={register}
          errors={errors}
          departments={departments}
          disabled={isLoading}
          selectedDepartmentIds={selectedDepartmentIds}
          onDepartmentChange={handleDepartmentChange}
          darkMode={darkMode}
        />

        {/* Vehicle - Only for new employees, same inline-add pattern as visitor registration */}
        {!editingEmployee && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-[11px] font-semibold tracking-widest uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Veículo
              </label>
              {!showVehicleForm && vehicleList.length === 0 && (
                <button
                  type="button"
                  onClick={() => setShowVehicleForm(true)}
                  disabled={isLoading || isSubmitting}
                  className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
                    darkMode
                      ? 'border-primary-500 text-primary-400 hover:bg-primary-900/30'
                      : 'border-primary-600 text-primary-700 hover:bg-primary-50'
                  }`}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Adicionar Veículo
                </button>
              )}
            </div>

            {showVehicleForm && (
              <div className={`rounded-xl border p-3 mb-2 ${darkMode ? 'bg-gray-700/50 border-gray-500' : 'bg-white border-gray-300 shadow-sm'}`}>
                <div className="flex gap-3 items-end">
                  <div className="flex-1 min-w-0">
                    <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Placa *</label>
                    <input
                      type="text"
                      value={newVehiclePlate}
                      onChange={(e) => setNewVehiclePlate(e.target.value.toUpperCase())}
                      placeholder="ABC1D23"
                      maxLength={8}
                      autoFocus
                      className={`block w-full rounded-lg py-2 px-3 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-gray-50 border border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Modelo</label>
                    <input
                      type="text"
                      value={newVehicleModel}
                      onChange={(e) => setNewVehicleModel(e.target.value)}
                      placeholder="Ex: Civic"
                      className={`block w-full rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-gray-50 border border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cor</label>
                    <input
                      type="text"
                      value={newVehicleColor}
                      onChange={(e) => setNewVehicleColor(e.target.value)}
                      placeholder="Ex: Preto"
                      className={`block w-full rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-gray-50 border border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => { setShowVehicleForm(false); setNewVehiclePlate(''); setNewVehicleModel(''); setNewVehicleColor(''); }}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${darkMode ? 'border-gray-500 text-gray-300 hover:bg-gray-600' : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const plate = newVehiclePlate.trim();
                        if (!plate) return;
                        setVehicleList([{ plate, model: newVehicleModel.trim(), color: newVehicleColor.trim() }]);
                        setShowVehicleForm(false);
                        setNewVehiclePlate(''); setNewVehicleModel(''); setNewVehicleColor('');
                      }}
                      disabled={!newVehiclePlate.trim()}
                      className="px-3 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition-colors"
                    >
                      <CheckCircle className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {vehicleList.length > 0 && (
              <div className={`rounded-xl border overflow-hidden divide-y ${darkMode ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-100'}`}>
                {vehicleList.map((v, i) => (
                  <div key={i} className={`flex items-center gap-3 px-4 py-3 ${darkMode ? 'bg-gray-800/30' : 'bg-white'}`}>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <Car className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <LicensePlate plate={v.plate} size="sm" />
                      {(v.model || v.color) && (
                        <p className={`text-xs mt-0.5 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{[v.model, v.color].filter(Boolean).join(' · ')}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setVehicleList([])}
                      className={`p-1 rounded transition-colors flex-shrink-0 ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-gray-700' : 'text-gray-400 hover:text-red-500 hover:bg-gray-200'}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {vehicleList.length === 0 && !showVehicleForm && (
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nenhum veículo adicionado</p>
            )}
          </div>
        )}
      </RegistrationModal>

      {/* Identity photo — upload / webcam */}
      {showIdPhotoModal && (
        <PhotoCaptureModalEnhanced
          open={showIdPhotoModal}
          onClose={() => setShowIdPhotoModal(false)}
          onConfirm={(result) => { setPhoto(result); setShowIdPhotoModal(false); }}
          title="Foto de Identificação"
          cropShape="rect"
          aspectRatio={4 / 5}
          customerId={customerId || undefined}
          initialTab={idPhotoModalTab}
          hideIPCamera
        />
      )}

      {/* Identity photo — IP camera */}
      {showIdPhotoIPModal && (
        <PhotoIPCameraModal
          open={showIdPhotoIPModal}
          onClose={() => setShowIdPhotoIPModal(false)}
          onConfirm={(result: IPCameraPhotoResult) => {
            setPhoto(result as unknown as PhotoCaptureResult);
            setShowIdPhotoIPModal(false);
          }}
          title="Foto de Identificação"
          customerId={customerId || undefined}
          filterCategory="captura_facial"
          cropShape="rect"
          aspectRatio={4 / 5}
        />
      )}

      {/* Doc front — upload / webcam */}
      {showDocFrontModal && (
        <PhotoCaptureModalEnhanced
          open={showDocFrontModal}
          onClose={() => setShowDocFrontModal(false)}
          onConfirm={(result) => { setDocumentFrontPhoto(result); setShowDocFrontModal(false); }}
          title="Documento — Frente"
          cropShape="rect"
          customerId={customerId || undefined}
          initialTab={docFrontModalTab}
          hideIPCamera
        />
      )}

      {/* Doc front — IP camera */}
      {showDocFrontIPModal && (
        <PhotoIPCameraModal
          open={showDocFrontIPModal}
          onClose={() => setShowDocFrontIPModal(false)}
          onConfirm={(result: IPCameraPhotoResult) => {
            setDocumentFrontPhoto(result as unknown as PhotoCaptureResult);
            setShowDocFrontIPModal(false);
          }}
          title="Documento — Frente"
          customerId={customerId || undefined}
          filterCategory="documento"
          cropShape="rect"
          aspectRatio={3 / 2}
        />
      )}

      {/* Doc back — upload / webcam */}
      {showDocBackModal && (
        <PhotoCaptureModalEnhanced
          open={showDocBackModal}
          onClose={() => setShowDocBackModal(false)}
          onConfirm={(result) => { setDocumentBackPhoto(result); setShowDocBackModal(false); }}
          title="Documento — Verso"
          cropShape="rect"
          customerId={customerId || undefined}
          initialTab={docBackModalTab}
          hideIPCamera
        />
      )}

      {/* Doc back — IP camera */}
      {showDocBackIPModal && (
        <PhotoIPCameraModal
          open={showDocBackIPModal}
          onClose={() => setShowDocBackIPModal(false)}
          onConfirm={(result: IPCameraPhotoResult) => {
            setDocumentBackPhoto(result as unknown as PhotoCaptureResult);
            setShowDocBackIPModal(false);
          }}
          title="Documento — Verso"
          customerId={customerId || undefined}
          filterCategory="documento"
          cropShape="rect"
          aspectRatio={3 / 2}
        />
      )}

      {/* Photo Zoom Modal — filled-state click on any PhotoUploadArea. Portaled to #modal-root
       * (absolute, not fixed) to avoid the SwiftShader black-paint bug when fixed positioning
       * sits inside overflow-y-auto */}
      {zoomPhotoUrl && ReactDOM.createPortal(
        <div
          className="absolute inset-0 bg-black/80 z-[9999] flex items-center justify-center p-6"
          onClick={() => { setZoomPhotoUrl(null); setZoomPhotoActions(null); }}
        >
          <div className="relative max-w-[90vw] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            {zoomPhotoTitle && (
              <div className="text-white font-semibold text-lg text-center">{zoomPhotoTitle}</div>
            )}
            <div className="relative">
              <img
                src={zoomPhotoUrl}
                alt={zoomPhotoTitle}
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              />
              <button
                onClick={() => { setZoomPhotoUrl(null); setZoomPhotoActions(null); }}
                className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white text-gray-700 shadow-lg hover:bg-gray-100 transition-colors"
                title="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {zoomPhotoActions && (
              <div className="flex gap-3 mt-1">
                <button
                  onClick={() => { setZoomPhotoUrl(null); setZoomPhotoActions(null); zoomPhotoActions.onReplace(); }}
                  className="px-5 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium transition-colors"
                >
                  Trocar foto
                </button>
                <button
                  onClick={() => { setZoomPhotoUrl(null); setZoomPhotoActions(null); zoomPhotoActions.onRemove(); }}
                  className="px-5 py-2 rounded-lg bg-red-500/70 hover:bg-red-500/90 text-white text-sm font-medium transition-colors"
                >
                  Remover
                </button>
              </div>
            )}
          </div>
        </div>,
        document.getElementById('modal-root') ?? document.body
      )}

      {/* All photos editor */}
      {showAllPhotosModal && (
        <AllPhotosEditModal
          open={showAllPhotosModal}
          onClose={() => setShowAllPhotosModal(false)}
          onSave={(photos) => {
            if (photos.identity) setPhoto(photos.identity as unknown as PhotoCaptureResult);
            if (photos.document_front) setDocumentFrontPhoto(photos.document_front as unknown as PhotoCaptureResult);
            if (photos.document_back) setDocumentBackPhoto(photos.document_back as unknown as PhotoCaptureResult);
            setShowAllPhotosModal(false);
          }}
          currentPhotos={{
            identity: photo as unknown as PhotoResult | null,
            document_front: documentFrontPhoto as unknown as PhotoResult | null,
            document_back: documentBackPhoto as unknown as PhotoResult | null,
          }}
          customerId={customerId || undefined}
          darkMode={darkMode}
          columns={[
            { type: 'identity', label: 'Foto', shape: 'circle' },
            { type: 'document_front', label: 'Doc. Frente', shape: 'rect' },
            { type: 'document_back', label: 'Doc. Verso', shape: 'rect' },
          ]}
        />
      )}
    </>
  );
};

export default EmployeeRegistrationModal;
